import classNames from "classnames"
import React from "react"

import { Color } from "../../../../../constants/V2/color"
import BulletItem from "../../../../elements/V2/BulletItem"
import PillButton from "../../../../elements/V2/Buttons/PillButton"
import TextButton from "../../../../elements/V2/Buttons/TextButton"
import GatsbyStoryblokImage from "../../../../elements/V2/GatsbyStoryblokImage"
import ArrowIcon from "../../../../elements/V2/Icons/ArrowIcon"
import Typography from "../../../../elements/V2/Typography"
import LayoutItem from "../../../Layouts/LayoutItem"

import { useScrollAnimateSelf } from "@hooks/V2/useScrollAnimate"
import {
  getAccentColor,
  getBackgroundColorClass,
  getGradientFromColorClass,
  getGradientToColorClass,
} from "@utils/V2/color"

type Props = {
  cardWidth?: "6-col" | "8-col" | "12-col"
  primaryBackgroundColor: Color
  secondaryBackgroundColor?: Color
  superscriptText?: string
  titleText: string
  titleSize?: "h3" | "lead-lg"
  descriptionText?: string
  imageUrl: string
  imageAlt: string
  iconUrl?: string
  iconAlt?: string
  buttonText?: string
  buttonLink?: string
  buttonBorderColor?: Color
  bulletPointList?: React.ComponentProps<typeof BulletItem>[]
  mobileGridOrder?: number
}

const BenefitMaster = ({
  cardWidth = "8-col",
  primaryBackgroundColor,
  secondaryBackgroundColor,
  superscriptText,
  titleText,
  titleSize = "h3",
  descriptionText,
  imageUrl,
  imageAlt,
  iconUrl,
  iconAlt,
  buttonText,
  buttonLink,
  buttonBorderColor,
  bulletPointList,
  mobileGridOrder,
  ...props
}: Props) => {
  const { animationRefCallback } = useScrollAnimateSelf(true)

  return (
    <LayoutItem
      as="div"
      mobileGridOrder={mobileGridOrder}
      ref={animationRefCallback}
      className={classNames(
        "rounded-lg col-span-full grid grid-cols-1 grid-rows-[max-content_1fr] overflow-hidden sm:col-span-2 md:grid-cols-2 md:grid-rows-1",
        !secondaryBackgroundColor
          ? getBackgroundColorClass(primaryBackgroundColor)
          : [
              "bg-gradient-to-br",
              getGradientFromColorClass(primaryBackgroundColor),
              getGradientToColorClass(secondaryBackgroundColor),
            ],
        cardWidth === "12-col" ? "sm:col-span-full" : "sm:col-span-2",
        { "md:col-span-3 lg:col-span-6": cardWidth === "6-col" },
        { "md:col-span-4 lg:col-span-8": cardWidth === "8-col" }
      )}
      {...props}
    >
      <div className="flex flex-col items-start justify-between gap-8 px-24 py-32 md:gap-16 md:py-32 md:pl-32 md:pr-0 lg:gap-20 lg:py-48 lg:pl-48">
        <div className="flex flex-col gap-8 md:gap-16 lg:gap-20">
          {superscriptText && (
            <Typography
              font="grotesk"
              weight="medium"
              size="subscript-lg"
              color={getAccentColor(primaryBackgroundColor)}
              text={superscriptText}
              className="uppercase"
            />
          )}
          <Typography
            font="grotesk"
            weight="medium"
            size={titleSize}
            color={getAccentColor(primaryBackgroundColor)}
            text={titleText}
            element="h3"
          />
          {descriptionText && (
            <Typography
              font="grotesk"
              weight="book"
              size="body-lg"
              color={getAccentColor(primaryBackgroundColor)}
              text={descriptionText}
            />
          )}
          {bulletPointList && (
            <div className="flex flex-col gap-4">
              {bulletPointList.map((bulletItem, index) => (
                <BulletItem
                  key={`${bulletItem.text.replace(" ", "-")}-${index}`}
                  text={bulletItem.text}
                  textColor={getAccentColor(primaryBackgroundColor)}
                  iconOverrideColor={getAccentColor(primaryBackgroundColor)}
                />
              ))}
            </div>
          )}
        </div>
        {buttonText && buttonLink ? (
          buttonBorderColor ? (
            <PillButton
              size="large"
              style="bordered"
              text={buttonText}
              linkUrl={buttonLink}
              color={buttonBorderColor}
              overrideTextColor={getAccentColor(primaryBackgroundColor)}
            />
          ) : (
            <TextButton
              style="icon"
              icon={<ArrowIcon />}
              text={buttonText}
              linkUrl={buttonLink}
              color={getAccentColor(primaryBackgroundColor)}
            />
          )
        ) : null}
      </div>
      <div className="relative">
        <div
          className={classNames(
            "bottom-0 left-0 right-0 flex h-full w-full items-center justify-center md:absolute",
            cardWidth === "6-col" ? "aspect-[3/4]" : "aspect-square"
          )}
        >
          <GatsbyStoryblokImage
            aspectRatio={cardWidth === "6-col" ? "3:4" : "1:1"}
            image={imageUrl}
            alt={imageAlt || `${titleText}-image`}
            className="h-full w-full"
          />
        </div>
        {iconUrl && (
          <div className="absolute right-24 top-24 md:right-40 md:top-40 lg:right-56 lg:top-56">
            <GatsbyStoryblokImage
              image={iconUrl}
              alt={iconAlt || `${titleText}-icon`}
            />
          </div>
        )}
      </div>
    </LayoutItem>
  )
}

export default BenefitMaster
